/**
 * Recursive space/item renderer.
 *
 * Each space is a 0..SPACE_SIZE × 0..SPACE_SIZE canvas. Items position absolutely
 * within it. A nested space is drawn inside its item's frame using a scale
 * transform — so deep zoom compounds through the DOM naturally.
 *
 * Visibility culling happens in two places:
 *  - screen-size of an item: tiny items render as a dot; below ~1px, skip.
 *  - nested space: only recurse if the frame is big enough to be useful.
 */
import { createElement, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, FOCUS_RING } from "@archlang/ui";
import { propNameToAttr, propValueToAttr } from "@archlang/scene/arch-model";
import {
  type BoardEdge,
  type BoardItem,
  type BoardSpace,
  type EdgeWidgetStyle,
  type PlacedGroupFrame,
} from "@archlang/scene/board";
import { DebugOverlay } from "./DebugOverlay";
import { BoardOverlayLayers } from "./BoardOverlayLayers.js";
import { svgFragmentUrl } from "../svgFragmentUrl";
import { useHovered } from "./hoverStore";
import { useLifecyclePaint } from "./lifecycle/useLifecycleState.js";
import { GroundSubtreeContext, useBoardFocus, useInsideGround } from "./focus/useBoardFocus.js";
import { edgeLifecycleStyle } from "@archlang/scene/board";
import { useHoveredConnections, useConnectionHovered, useConnectionHoverStore } from "./connectionHoverStore.js";
import { useSelectedConnections, useConnectionSelected, useConnectionSelectionStore } from "./connectionSelectionStore.js";
import { NODE_DRAG_CLAIM_EVENT } from "./viewportHooks";
import { TemplateNode, WidgetNode } from "./WidgetNode";
import { bandCssVars } from "./lodBands.js";
import { widgetForModule } from "./widgetResolve.js";
import { BUILTIN_MODULE_TAG } from "./builtinWidgets.js";
import { nestedFloorRect, projectsNestedSpace } from "./nestedProjection.js";
import { edgeBadgeProps } from "./edgeBadge.js";
import { iconSvg } from "../generated/stdlib-widgets/arch.ui/icons.js";
import { subspaceSlotRect } from "@archlang/scene/board";
import type { Module } from "@archlang/scene/arch-model";
import type { EdgeDeco, EdgeDecoMap } from "../process/edgeDecoration.js";
import { aggregateEdgeDeco, chipLabelFor } from "../process/edgeDecoration.js";
import {
  type Anchor as SharedAnchor,
  type Rect as SharedRect,
  type Side as SharedSide,
} from "@archlang/scene/board";
import { planRoutes, connectionIdsFromDetails, logicalKeyOf, FOCUS_VEIL, FOCUS_GROUND_ALPHA, groundStrokeOpacity } from "@archlang/scene/board";
import { resolvePortPlacements, portConnectorPath, portPillBox, portGlyph, PORT_W, PORT_H } from "./portPlacement.js";

// Below 1px of projected width an item is culled entirely (perf). All
// other LOD branching lives in widget CSS via the continuous
// --arch-band-* vars; there are no discrete zoom-level attributes any
// more — widgets decide visibility/scale purely from band signals.
const THRESHOLD_SKIP = 1;

// Safari/WebKit resolves a fragment-only reference like `url(#arrow-default)`
// in `marker-end` against the *document* URL, and silently fails to find the
// in-document marker once that URL carries a query string — see
// ../svgFragmentUrl (shared with the swimlane + debug overlays).
const markerUrl = svgFragmentUrl;

// Arrowhead markers are EXPLICITLY filled per colour rather than via
// `fill="context-stroke"`: Safari/WebKit does not support context-stroke
// (Chromium 119+/Firefox only) and renders such arrowheads invisible. Each
// distinct edge stroke colour gets its own <marker> in the layer's <defs>,
// id-keyed by the colour string so a path can always reference the marker
// matching its stroke.
export function colorKey(color: string): string {
  // Encode each non-alphanumeric as `_<charCode>_` — a valid id fragment that is
  // COLLISION-FREE: distinct colour strings map to distinct keys (a stripped
  // form let `rgb(1,23)` and `rgb(12,3)` collide → two connections sharing one
  // <marker> id → the second's arrowhead rendered in the wrong colour).
  return color.replace(/[^a-zA-Z0-9]/g, (c) => `_${c.charCodeAt(0)}_`);
}
function arrowMarkerId(stroke: string): string {
  return `arrow-c-${colorKey(stroke)}`;
}


export interface ItemDiagnostic {
  readonly severity: "error" | "warning" | "info" | "hint";
  readonly message: string;
  /** Source location of THIS diagnostic (not the owning module's declaration).
   *  Drives the issue popover's "jump to the exact spot in code" click. When
   *  absent the host falls back to the module declaration. 1-based line/column
   *  to match the editor reveal. */
  readonly path?: string;
  readonly line?: number;
  readonly column?: number;
}

export interface ItemRenderContext {
  readonly selectedId: string | null;
  readonly selectedEdgeId: string | null;
  readonly activeProcessId: string | null;
  /** Highlight only this step of the active process (edges at this step get
   *  extra emphasis). null = show all process steps equally. */
  readonly activeStepIndex: number | null;
  readonly highlighted: ReadonlySet<string>;
  /** Node-only highlight (legend aspect members). Does NOT light edges. */
  readonly memberHighlighted?: ReadonlySet<string>;
  /** Qualified interface paths whose edges should highlight (label aspect on
   *  the interface itself). */
  readonly memberInterfaces?: ReadonlySet<string>;
  readonly diagnosticsByItem: ReadonlyMap<string, readonly ItemDiagnostic[]>;
  /** Click a diagnostic in the issue popover → open it in the editor at its
   *  exact source location and focus the cursor there. Opt-in (the viewer wires
   *  it; static embeds leave it undefined → popover rows are non-clickable). */
  readonly onOpenDiagnostic?: (item: BoardItem, diag: ItemDiagnostic) => void;
  /** When true, each space renders the layout debug overlay. */
  readonly debugLayout?: boolean;
  readonly onSelect: (id: string) => void;
  readonly onSelectEdge: (edgeId: string) => void;
  readonly onDive: (item: BoardItem) => void;
  /** Cmd/Ctrl-click: open this item in a new board tab focused on it. */
  readonly onOpenRooted?: (item: BoardItem) => void;
  /** Right-click: open a context menu for this item at the pointer (viewport
   *  coords). When set, replaces the default unpin-on-right-click. */
  readonly onItemContextMenu?: (item: BoardItem, x: number, y: number) => void;
  /** Right-click a connection/link or an interface port → open its menu at the
   *  pointer. `edgeId` is the connection this segment/port belongs to. */
  readonly onEdgeContextMenu?: (edgeId: string, x: number, y: number) => void;
  readonly onPlace: (id: string, bounds: { x: number; y: number; w: number; h: number }) => void;
  /** Click-reveal (VIEWS §7): a boundary stub was clicked — reveal its
   *  absent peer in the active view. Optional; stubs fall back to the
   *  connection-selection click when absent. */
  readonly onRevealBoundary?: (peerKey: string) => void;
  /** Live-zoom / quantized-zoom ratio at drag time. The `scale` prop items
   *  render with is the QUANTIZED (gesture-snapshot) zoom; dividing pointer
   *  deltas by it alone misplaces a drag by up to ~25% at low zoom. The
   *  canvas supplies this stable callback so drags track the live camera. */
  readonly dragZoomCorrection?: () => number;
  /** Right-click: removes the item from hand-placements so the solver
   *  repositions it. Only meaningful for currently-pinned items; others
   *  ignore it. */
  readonly onUnpin: (id: string) => void;
  /** Set of item ids that are currently pinned by hand placement. */
  readonly pinnedIds: ReadonlySet<string>;
  /** When non-null, the matching item renders with a ghost-repair highlight
   *  (dashed yellow outline, reduced opacity). Driven by the animation
   *  loop during the ghost / ghostSettle solver phases — null otherwise. */
  readonly ghostMoverId?: string | null;
  /** Edge decoration map from the process walker — drives chips, badges, pulses. */
  readonly edgeDecoMap?: EdgeDecoMap;
  /** Node performing the current SELF-REFERENTIAL step (self-call / note /
   *  await) — gets a pulsing ring, since no edge carries the highlight. */
  readonly stepActorId?: string | null;
}

export interface SpaceRendererProps {
  readonly space: BoardSpace;
  /** Effective pixels per unit of this space's coordinate system. */
  readonly scale: number;
  /** Clip rect in this space's coords, for culling. undefined → no culling. */
  readonly viewBounds?: { x1: number; y1: number; x2: number; y2: number };
  readonly depth: number;
  readonly ctx: ItemRenderContext;
  /**
   * Gravity-well context layer: this render is the focal's PARENT drawn
   * behind the focal to keep the outer world visible on dive. It is
   * non-interactive (drag/hover/ports off — distinct from `depth`, which
   * is 0 for both the focal and this context root) and it suppresses the
   * focal container's interior via `holeChildId` so the focal layer shows
   * through cleanly. See BoardCanvas' context-layer mount.
   */
  readonly context?: boolean;
  /** Id of the focal container within this (parent) space whose nested
   *  space must NOT be re-rendered — the focal layer draws its interior on
   *  top. Only meaningful together with `context`. */
  readonly holeChildId?: string;
  /** Floor + grid extents override (space-local coords) — a nested preview
   *  passes the whole slot rect (`nestedFloorRect`) so the well fills the
   *  region dedicated to the subspace instead of shrink-wrapping the content.
   *  Absent (the focal / context layers) → the space's own frame. */
  readonly floorRect?: { x: number; y: number; w: number; h: number };
}

export const SpaceRenderer = memo(function SpaceRenderer({
  space,
  scale,
  viewBounds,
  depth,
  ctx,
  context = false,
  holeChildId,
  floorRect,
}: SpaceRendererProps) {
  // Stable across pans: items only change when the layout does, so panning
  // (which re-renders this memo'd component via viewBounds) must not
  // reallocate the map — that churn is what drives GC stutter on big pans.
  const edgeMap = useMemo(
    () => new Map(space.items.map((it) => [it.id, it] as const)),
    [space.items]
  );

  return (
    <div
      className="absolute inset-0"
      data-board-space={space.id}
    >
      {/* Space "floor": the dark rounded carpet + border the nodes sit on. Drawn
          for EVERY space (focal and nested preview alike) so a space's background
          is identical whether you're inside it or looking at it as a preview.
          OPAQUE fill (not a translucent tint): a translucent floor blends with
          whatever is behind it, and that differs by how many ancestor floors sit
          under it (a child's floor has the parent's floor behind it as the focal,
          but bare canvas behind it as a context preview) — so its colour shifted
          when you dived a level deeper. An opaque floor REPLACES what's behind it,
          so its colour is the same at every depth. Behind the grid + nodes. */}
      <div
        className="absolute rounded-3xl border border-slate-700/(--tint-dim)"
        data-export-omit
        style={{
          left: floorRect?.x ?? space.originX,
          top: floorRect?.y ?? space.originY,
          width: floorRect?.w ?? space.width,
          height: floorRect?.h ?? space.height,
          // Themed via the live data-theme CSS vars (a plain DOM node, so the
          // var resolves live — no recompose needed, unlike the scene HTML). One
          // step above the canvas (--slate-950) so the floor reads as a surface.
          backgroundColor: "rgb(var(--slate-900))",
          boxShadow: "0 0 0 1px rgb(var(--slate-950) / 0.4) inset",
        }}
      />
      {/* Background grid — subtle texture indicating "space". Per-space by design:
          different spaces render dots at their own scale, and that size difference
          across levels is expected. */}
      <GridBackdrop
        scale={scale}
        originX={floorRect?.x ?? space.originX}
        originY={floorRect?.y ?? space.originY}
        width={floorRect?.w ?? space.width}
        height={floorRect?.h ?? space.height}
      />

      {/* `group by` frames — background rects UNDER items and edges (frame
          groups). Solver-guaranteed disjoint; pure geometry from
          BoardSpace.groupFrames. The label is the interaction handle (hover
          highlight + drag-moves-members) on the focal layer only. */}
      {space.groupFrames && space.groupFrames.length > 0 && (
        <GroupFramesLayer
          frames={space.groupFrames}
          items={edgeMap}
          interactive={depth === 0 && !context}
          scale={scale}
          ctx={ctx}
        />
      )}

      {/* Items */}
      {space.items.map((item) => {
        if (viewBounds && !rectsOverlap(item, viewBounds)) return null;
        const projW = item.w * scale;
        if (projW < THRESHOLD_SKIP) return null;
        return (
          <ItemRenderer
            key={item.id}
            item={item}
            scale={scale}
            depth={depth}
            ctx={ctx}
            context={context}
            hole={context && item.id === holeChildId}
          />
        );
      })}

      {/* Edges — drawn on top in the space's own coord system */}
      {space.edges && space.edges.length > 0 && (
        <EdgesLayer
          edges={space.edges}
          items={edgeMap}
          ctx={ctx}
          spaceW={space.width}
          spaceH={space.height}
          viewBounds={viewBounds}
          scale={scale}
          idSuffix={space.id}
        />
      )}

      {/* Ghost nodes — cross-boundary peers rendered as labels inside this
          space. Rendered whenever the space itself renders (gated upstream by
          INNER_RENDER_SCALE) so they reveal in LOCKSTEP with the space's own
          nodes/edges — they sit inside the same band-opacity wrapper and fade
          with it. Never on the context surround. */}
      {!context && (
        <PortsLayer
          items={space.items}
          ctx={ctx}
          spaceW={space.width}
          spaceH={space.height}
          originX={space.originX}
          originY={space.originY}
        />
      )}

      {/* Debug overlay — shows degree, forces, edge stretches, repulsion
          lines, summary pill. Only at the focal layer so nested previews
          don't get cluttered. */}
      {depth === 0 && !context && ctx.debugLayout && (
        <DebugOverlay
          space={space}
          scale={scale}
          selectedId={ctx.selectedId}
        />
      )}
    </div>
  );
});

function rectsOverlap(
  a: { x: number; y: number; w: number; h: number },
  b: { x1: number; y1: number; x2: number; y2: number },
) {
  return !(a.x + a.w < b.x1 || a.x > b.x2 || a.y + a.h < b.y1 || a.y > b.y2);
}

function rectInView(
  a: { x: number; y: number; w: number; h: number },
  b: { x1: number; y1: number; x2: number; y2: number },
) {
  return rectsOverlap(a, b);
}

// Fixed-in-space dot grid. The pattern is anchored to the space's local
// coordinates and scales with the content transform, so zooming no longer
// makes dots slide or breathe — they read as a stable surface. Per-space:
// nested spaces render their own grid at their own scale.
function GridBackdrop({
  scale,
  originX,
  originY,
  width,
  height,
}: {
  scale: number;
  originX: number;
  originY: number;
  width: number;
  height: number;
}) {
  void scale;
  return (
    <div
      className="absolute pointer-events-none"
      data-export-omit
      style={{
        left: originX,
        top: originY,
        width,
        height,
        backgroundImage:
          "radial-gradient(circle, rgb(var(--slate-400) / 0.18) 1.2px, transparent 1.5px)",
        backgroundSize: "32px 32px",
      }}
    />
  );
}

interface ItemRendererProps {
  readonly item: BoardItem;
  readonly scale: number;
  readonly depth: number;
  readonly ctx: ItemRenderContext;
  /** Rendered on the non-interactive gravity-well context layer (parent surround). */
  readonly context?: boolean;
  /** This item is the focal container on the context layer — render frame-only
   *  (no nested space) so the focal layer shows through. */
  readonly hole?: boolean;
}

/** `group by` frame rects (frame groups) — the live twin of the headless
 *  renderer's frames layer. Pure geometry from `BoardSpace.groupFrames`;
 *  paint via theme classes (live board rule: classes, not scene tokens).
 *  The frame BODY never takes pointer events (it would swallow board pans);
 *  the LABEL is the handle: hover highlights the frame, dragging it moves
 *  every member through the same `onPlace` channel a node drag uses — one
 *  gesture, one undo entry (usePlacements collapses the burst). */
export const GroupFramesLayer = memo(function GroupFramesLayer({
  frames,
  items,
  interactive,
  scale,
  ctx,
}: {
  readonly frames: readonly PlacedGroupFrame[];
  readonly items: ReadonlyMap<string, BoardItem>;
  readonly interactive: boolean;
  readonly scale: number;
  readonly ctx: ItemRenderContext;
}) {
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  // A frame is figure iff it holds ANY figure member (§3 containment) — its
  // members carry their own veils, so all this fades is the frame's own
  // backdrop and label, which would otherwise stay bright around receded nodes.
  const boardFocus = useBoardFocus();
  const groundFrame = (f: PlacedGroupFrame): boolean => {
    if (!boardFocus) return false;
    // Only members this space actually places have a role. `every` over a frame
    // whose members are all elsewhere is vacuously true, which would fade a
    // frame nothing in it was judged on — and the export decides the same
    // question by requiring at least one resolved member.
    const present = f.memberIds.map((id) => items.get(id)).filter((it) => !!it);
    return present.length > 0 && present.every((it) => boardFocus.role(it!) === "ground");
  };
  const dragRef = useRef<{
    frameId: string;
    pointerId: number;
    startX: number;
    startY: number;
    orig: Map<string, { x: number; y: number; w: number; h: number }>;
  } | null>(null);

  const onLabelDown = useCallback(
    (f: PlacedGroupFrame) => (e: React.PointerEvent) => {
      if (!interactive || e.button !== 0) return;
      // Touch stays a board pan — the label is too small for the long-press
      // arbitration to earn its keep; mouse/pen drag immediately.
      if (e.pointerType === "touch") return;
      e.stopPropagation();
      try {
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      } catch {
        /* jsdom / detached element */
      }
      const orig = new Map<string, { x: number; y: number; w: number; h: number }>();
      for (const id of f.memberIds) {
        const it = items.get(id);
        if (it) orig.set(id, { x: it.x, y: it.y, w: it.w, h: it.h });
      }
      dragRef.current = {
        frameId: f.id,
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        orig,
      };
    },
    [interactive, items],
  );
  const onLabelMove = useCallback(
    (e: React.PointerEvent) => {
      const d = dragRef.current;
      if (!d || d.pointerId !== e.pointerId) return;
      const liveScale = scale * (ctx.dragZoomCorrection?.() ?? 1);
      const dx = (e.clientX - d.startX) / liveScale;
      const dy = (e.clientY - d.startY) / liveScale;
      for (const [id, r] of d.orig) {
        ctx.onPlace(id, { x: r.x + dx, y: r.y + dy, w: r.w, h: r.h });
      }
    },
    [ctx, scale],
  );
  const onLabelUp = useCallback((e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d || d.pointerId !== e.pointerId) return;
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* already released */
    }
    dragRef.current = null;
  }, []);

  return (
    <>
      {frames.map((f) => (
        <div
          key={f.id}
          data-frame-id={f.id}
          className={`absolute rounded-2xl border border-dashed pointer-events-none ${
            hoveredId === f.id
              ? "border-slate-400/(--tint-heavy) bg-slate-700/(--tint-soft)"
              : "border-slate-500/50"
          }`}
          style={{
            left: f.x,
            top: f.y,
            width: f.w,
            height: f.h,
            ...(groundFrame(f) ? { opacity: FOCUS_GROUND_ALPHA } : {}),
            // Depth-stepped fill (nested `group by`): outer lighter, inner
            // stronger — array order (depth asc) is the paint order.
            ...(hoveredId === f.id
              ? {}
              : {
                  backgroundColor: `rgb(30 41 59 / ${
                    0.2 + Math.min(3, f.depth ?? 0) * 0.08
                  })`,
                }),
          }}
        >
          <span
            data-frame-handle={f.id}
            className={`absolute left-3 top-1 text-xs font-semibold select-none ${
              hoveredId === f.id ? "text-slate-200" : "text-slate-400"
            } ${interactive ? "pointer-events-auto cursor-grab" : ""}`}
            onMouseEnter={interactive ? () => setHoveredId(f.id) : undefined}
            onMouseLeave={interactive ? () => setHoveredId((h) => (h === f.id ? null : h)) : undefined}
            onPointerDown={onLabelDown(f)}
            onPointerMove={onLabelMove}
            onPointerUp={onLabelUp}
          >
            {f.label}
          </span>
        </div>
      ))}
    </>
  );
});

const ItemRenderer = memo(function ItemRenderer({
  item,
  scale,
  depth,
  ctx,
  context = false,
  hole = false,
}: ItemRendererProps) {
  // Doppelgänger identity: a `replicated`-mode copy resolves to its one logical
  // place, so selecting / hovering ANY copy lights every copy (and the real
  // node whose id IS the logical key). Ordinary items: logicalKey === id.
  const logicalKey = logicalKeyOf(item);
  const isSelected = logicalKey === ctx.selectedId;
  // Hover is React-driven by a single pointermove + hit-test on the
  // outer board container (see BoardCanvas). The container compares
  // event.target.closest('[data-board-item]') against item ids and
  // commits the hovered id to a tiny external store. We avoid CSS
  // `:host(:hover)` and per-item pointerenter/leave because both rely on
  // the same browser hit-test that Chrome can leave stale on GPU-
  // composited transformed layers (the pan/zoom container has
  // `will-change: transform`), and because per-item enter/leave would
  // light up parent + child whenever the cursor sits over a nested item.
  // Hit-testing via .closest keeps the deepest item authoritative.
  //
  // `useHovered` subscribes to the store with a boolean snapshot, so a
  // hover change re-renders only the two items whose state flips — not
  // the whole tree (which is what routing hover through `ctx` did).
  // Blast-radius / member highlight fan out to every copy too: a replicated
  // copy lights when its logical place is highlighted (consistent with hover +
  // minimap). `logicalKey === id` for ordinary items, so no behaviour change.
  const isHighlighted =
    ctx.highlighted.has(logicalKey) ||
    ctx.highlighted.has(item.id) ||
    (ctx.memberHighlighted?.has(logicalKey) ?? ctx.memberHighlighted?.has(item.id) ?? false);
  const hovered = useHovered(logicalKey);

  // Only items at the current focal (depth 0) are draggable / capture pointer.
  // Deeper items are "read-only" in the current focal — user must zoom into
  // their parent first to manipulate them. The context layer (gravity-well
  // surround) is depth 0 too but must stay non-interactive, so it's excluded
  // explicitly rather than by depth.
  const isFocal = depth === 0 && !context;

  const draggedRef = useRef(false);
  const dragRef = useRef<{
    startX: number;
    startY: number;
    origX: number;
    origY: number;
    pointerId: number;
    active: boolean;
  } | null>(null);

  // Touch node drag is long-press-armed: a plain finger drag over a node must
  // pan the BOARD (fingers are fat; nodes cover most of a phone screen), so
  // the immediate mouse-style drag would make touch panning impossible and
  // rearrange layouts by accident. Holding still ~half a second claims the
  // pointer from the board's touch-gesture layer (cancelable
  // NODE_DRAG_CLAIM_EVENT — refused when a pan/pinch already owns it), then
  // the regular drag path takes over.
  const longPressRef = useRef<{ timer: number; pointerId: number; x: number; y: number } | null>(null);
  const clearLongPress = useCallback(() => {
    if (longPressRef.current !== null) {
      clearTimeout(longPressRef.current.timer);
      longPressRef.current = null;
    }
  }, []);
  useEffect(() => clearLongPress, [clearLongPress]);
  const TOUCH_DRAG_LONG_PRESS_MS = 450;
  const TOUCH_DRAG_SLOP = 10;

  const onClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (draggedRef.current) {
        draggedRef.current = false;
        return;
      }
      // Cmd/Ctrl-click opens the item in its own focused board tab.
      if ((e.metaKey || e.ctrlKey) && ctx.onOpenRooted) {
        ctx.onOpenRooted(item);
        return;
      }
      ctx.onSelect(logicalKey);
    },
    [ctx, item, logicalKey],
  );
  const onDouble = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      // Universal: double-click always brings the item into view — the
      // context decides whether that's a dive (nested space) or a zoom-frame
      // (leaf module).
      ctx.onDive(item);
    },
    [ctx, item],
  );

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (!isFocal) return; // let container pan; click still selects
      if (e.button !== 0) return;
      const el = e.currentTarget as HTMLElement;
      if (e.pointerType === "touch") {
        // No capture yet — the board's touch layer pans/taps this pointer
        // until the long-press fires and successfully claims it.
        const { clientX, clientY, pointerId } = e;
        const origX = item.x;
        const origY = item.y;
        clearLongPress();
        dragRef.current = null;
        draggedRef.current = false;
        const timer = window.setTimeout(() => {
          longPressRef.current = null;
          const claim = new CustomEvent(NODE_DRAG_CLAIM_EVENT, {
            bubbles: true,
            cancelable: true,
            detail: { pointerId },
          });
          // preventDefault = a pan/pinch already owns the pointer.
          if (!el.dispatchEvent(claim)) return;
          try {
            el.setPointerCapture(pointerId);
          } catch {
            /* pointer already lifted / element detached */
          }
          dragRef.current = { startX: clientX, startY: clientY, origX, origY, pointerId, active: true };
          draggedRef.current = true;
          navigator.vibrate?.(15);
        }, TOUCH_DRAG_LONG_PRESS_MS);
        longPressRef.current = { timer, pointerId, x: clientX, y: clientY };
        return;
      }
      el.setPointerCapture(e.pointerId);
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        origX: item.x,
        origY: item.y,
        pointerId: e.pointerId,
        active: false,
      };
      draggedRef.current = false;
    },
    [isFocal, item.x, item.y, clearLongPress],
  );
  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      // A finger that travels is a board pan, not a press — disarm the
      // long-press. (Once the board layer captures the pointer for the pan
      // we stop seeing moves; the claim arbitration covers that case.)
      const lp = longPressRef.current;
      if (lp && lp.pointerId === e.pointerId &&
          Math.hypot(e.clientX - lp.x, e.clientY - lp.y) > TOUCH_DRAG_SLOP) {
        clearLongPress();
      }
      const d = dragRef.current;
      if (!d || d.pointerId !== e.pointerId) return;
      const screenDx = e.clientX - d.startX;
      const screenDy = e.clientY - d.startY;
      if (!d.active) {
        if (Math.hypot(screenDx, screenDy) < 4) return;
        d.active = true;
        draggedRef.current = true;
      }
      // `scale` is the quantized snapshot zoom (× nested fit products); the
      // correction rescales to the LIVE zoom so the node stays under the
      // pointer (the fit products cancel — only the zoom part is stale).
      const liveScale = scale * (ctx.dragZoomCorrection?.() ?? 1);
      const dx = screenDx / liveScale;
      const dy = screenDy / liveScale;
      // Commit on every move so edges, ports, and child content follow the
      // item live. Layout is cheap (O(n) grid) and placements is a shallow
      // map, so a 60fps re-render of the board tree is fine at this scale.
      ctx.onPlace(item.id, {
        x: d.origX + dx,
        y: d.origY + dy,
        w: item.w,
        h: item.h,
      });
    },
    [ctx, item.id, item.w, item.h, scale, clearLongPress],
  );
  const onPointerUp = useCallback(
    (e: React.PointerEvent) => {
      clearLongPress();
      const d = dragRef.current;
      if (!d || d.pointerId !== e.pointerId) return;
      const el = e.currentTarget as HTMLElement;
      try { el.releasePointerCapture(e.pointerId); } catch {}
      dragRef.current = null;
    },
    [clearLongPress],
  );

  const isGhostMover = !!ctx.ghostMoverId && ctx.ghostMoverId === item.id;
  const commonStyle: React.CSSProperties = {
    position: "absolute",
    left: item.x,
    top: item.y,
    width: item.w,
    height: item.h,
    ...(isGhostMover
      ? {
          outline: "2px dashed #facc15",
          outlineOffset: "4px",
          opacity: 0.6,
          transition: "opacity 80ms linear",
          zIndex: 30,
        }
      : null),
  };
  const ariaLabel = `${item.label}${item.space ? " (container)" : ""}`;
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        if (item.space) ctx.onDive(item);
        else ctx.onSelect(logicalKey);
      }
    },
    [ctx, item, logicalKey],
  );
  const onContextMenu = useCallback(
    (e: React.MouseEvent) => {
      // A host-provided context menu wins (board "Go to definition" etc.).
      if (ctx.onItemContextMenu) {
        e.preventDefault();
        e.stopPropagation();
        ctx.onItemContextMenu(item, e.clientX, e.clientY);
        return;
      }
      // Otherwise: right-click on a pinned node → unpin it (let the solver
      // reposition). Only fires if pinned, else the default browser menu shows.
      if (!ctx.pinnedIds.has(item.id)) return;
      e.preventDefault();
      e.stopPropagation();
      ctx.onUnpin(item.id);
    },
    [ctx, item],
  );
  const commonHandlers = isFocal
    ? ({
        "data-board-item": item.id,
        ...(item.logicalId ? { "data-logical-id": item.logicalId } : {}),
        "data-focal-item": "",
        role: "button",
        tabIndex: 0,
        "aria-label": ariaLabel,
        onClick,
        onDoubleClick: onDouble,
        onContextMenu,
        onKeyDown,
        onPointerDown,
        onPointerMove,
        onPointerUp,
        onPointerCancel: onPointerUp,
      } as const)
    : ({
        "data-board-item": item.id,
        ...(item.logicalId ? { "data-logical-id": item.logicalId } : {}),
        role: "button",
        tabIndex: -1,
        "aria-label": ariaLabel,
        onClick,
        onDoubleClick: onDouble,
      } as const);

  // Every module renders through a widget — `widgetForModule` falls
  // back to the built-in `arch-module` element when no `widget:` field
  // is configured. The widget receives the continuous projected width
  // as `--arch-projected-width`, the band signals as `--arch-band-*`
  // CSS vars, and live state as data attributes (`data-selected`,
  // `data-highlighted`). LOD lives entirely in widget CSS via the
  // band signals — the renderer just exposes them.
  const widget = widgetForModule(item.meta as Module);

  // View `focus` (VIEWS §2): figure/ground, a STANDING property of the view —
  // so it lives in its own channel rather than the transient-gesture socket,
  // where a legend hover used to erase it. Null context (no view focuses
  // anything) ⇒ no walk, no veil, nothing rendered.
  const focusRole = useBoardFocus()?.role(item);
  // ONE veil per ground subtree: an ancestor's veil already covers this item
  // (it is drawn over the whole item, nested space included), so drawing our
  // own would occlude twice and take the node from receded to unreadable.
  const insideGround = useInsideGround();
  const drawVeil = focusRole === "ground" && !insideGround;

  // Node colouring (aspect/label or diff) is read from NodeColoringContext
  // by WidgetNode/TemplateNode themselves — keyed by the module, so no
  // per-item plumbing here.

  // Render nested DOM when we're past the perf cutoff and have a child
  // space. Visibility (share of body) is CSS-driven via the band
  // signals — we hand off the children unconditionally above the
  // threshold and the widget's grow on [body] handles reveal.
  // focal container on the context layer draws its own interior (hole), so skip
  // the preview there; otherwise gate on scale/depth (M2.5).
  const hasInner = projectsNestedSpace(scale, depth, !!item.space, hole);
  // The scene widget (`arch-scene-module`) renders in LIGHT DOM (M3) and cannot
  // project a nested space through a shadow `<slot>` — so for it the nested space
  // is a positioned SIBLING (below), placed by the pure `subspaceSlotRect`. Other
  // (custom / user-authored) tag widgets keep the shadow-slot projection: the
  // nested content stays a light-DOM child of the element.
  const isSceneWidget = widget.kind === "tag" && widget.tag === BUILTIN_MODULE_TAG;
  const nestedSpaceContent =
    hasInner && item.space ? (
      // Wrapped once here so BOTH mount points below inherit it — the shadow
      // `<slot>` projection for a custom tag widget and the positioned sibling
      // for the light-DOM scene widget.
      <GroundSubtreeContext.Provider value={insideGround || focusRole === "ground"}>
        {renderNestedSpace(item, scale, depth, ctx, context, isSceneWidget)}
      </GroundSubtreeContext.Provider>
    ) : null;

  // DiagnosticBadge and InternalCallsLayer are siblings of the widget
  // host — they need to escape the host's bounds (badge sits above the
  // top-right corner; call badges overlay the body).
  return (
    <>
      {/* Diagnostic + process overlays belong to the interactive focal only —
          the context surround would double-stamp them behind the focal. */}
      {!context && (
        <DiagnosticBadge
          item={item}
          diagnostics={ctx.diagnosticsByItem.get(item.id)}
          onOpen={ctx.onOpenDiagnostic}
        />
      )}
      {!context && ctx.activeProcessId && item.internalCalls && item.internalCalls.length > 0 && (
        <InternalCallsLayer
          item={item}
          activeProcessId={ctx.activeProcessId}
          activeStepIndex={ctx.activeStepIndex}
          edgeDecoMap={ctx.edgeDecoMap}
          onSelect={() => ctx.onSelect(item.id)}
        />
      )}
      <div
        {...commonHandlers}
        style={commonStyle}
        className="absolute"
      >
        {/* Current-step actor ring: a SELF-referential step (self-call / note /
            await) draws no edge, so the halo lives on the acting node. */}
        {!context && ctx.stepActorId === item.id && (
          <div
            data-step-actor
            className="absolute -inset-2 rounded-xl pointer-events-none ring-4 ring-sky-400/80 animate-pulse"
          />
        )}
        {widget.kind === "tag" ? (
          <WidgetNode
            tag={widget.tag}
            spec={widget.spec}
            module={item.meta as Module}
            scale={scale}
            itemWidth={item.w}
            itemHeight={item.h}
            selected={isSelected}
            highlighted={isHighlighted}
            hovered={hovered}
            hasSubspace={!!item.space}
          >
            {/* Light-DOM scene widget: nested space is a sibling below, NOT a
                shadow-slot child. Custom tag widgets keep child projection. */}
            {isSceneWidget ? null : nestedSpaceContent}
          </WidgetNode>
        ) : (
          <TemplateNode
            template={widget.template}
            module={item.meta as Module}
            scale={scale}
            selected={isSelected}
            highlighted={isHighlighted}
            hovered={hovered}
          />
        )}
        {/* Scene widget's nested preview: a positioned sibling overlay (placed by
            the solved slot rect), so the element self-renders its card into light
            DOM without a shadow `<slot>`. */}
        {isSceneWidget ? nestedSpaceContent : null}
        {/* View `focus` — the GROUND veil, LAST in the item so it covers the
            widget AND the nested space drawn on top of it, and outside the
            widget so a lifecycle-ghosted node doesn't recede twice as far as a
            live one. Canvas-coloured (`bg-slate-950`, the board's own void), so
            the node is behind glass, not repainted: hue, dash, strike and badge
            all survive underneath and still trace to their legend row.
            Pointer-transparent — the ground is context, and still selectable. */}
        {drawVeil && (
          <div
            data-focus="ground"
            aria-hidden
            className="absolute inset-0 rounded-xl pointer-events-none bg-slate-950"
            style={{ opacity: FOCUS_VEIL }}
          />
        )}
      </div>
    </>
  );
});

/**
 * Render the nested-space JSX for an item that has children.
 *
 * Projected into the widget via its default <slot> (shadow widgets) or
 * placed as a positioned sibling at the solved slot rect (scene / light-DOM
 * widgets). Position and size come from PURE scene geometry
 * (`subspaceSlotRect`) — the same rect the dive/pop math anchors on
 * (`bodyRect.ts`) and the SVG export projects into, so render, dive and
 * export agree by construction. No ResizeObserver, no DOM measurement.
 */
function renderNestedSpace(
  item: BoardItem,
  scale: number,
  depth: number,
  ctx: ItemRenderContext,
  context: boolean,
  /** Scene widget (light DOM): the preview is a positioned SIBLING placed at the
   *  solved slot rect. Custom tag widget (shadow slot): the preview is a child
   *  filling the `[part=subspace]` slot (`inset-0`), so the rect's origin is the
   *  slot itself and only its size matters. */
  sibling: boolean,
): React.ReactNode {
  const innerSpace = item.space;
  if (!innerSpace) return null;
  // The nested-space slot rect comes from PURE scene geometry — the flex solver
  // already places the widget's `[part="subspace"]` slot, the exact rect the SVG
  // export projects into. No ResizeObserver, so `fit` is exact and synchronous:
  // no fit=0 first frame, no per-level measurement lag on deep spaces (the "deep
  // spaces show up much later than shallow ones" bug the old approx-seed + RO
  // existed to soften).
  const slot = subspaceSlotRect(item);
  const bandVars = bandCssVars(scale);
  return (
    <NestedSpaceFit
      key={innerSpace.id}
      innerSpace={innerSpace}
      slotX={slot.x}
      slotY={slot.y}
      slotW={slot.w}
      slotH={slot.h}
      sibling={sibling}
      // Pass the HOST scale. NestedSpaceFit multiplies by the pure `fit`, so the
      // child's `scale` prop is `scale · fit` — its TRUE cumulative on-screen
      // scale, which LOD bands key on (so nested detail reveals by cumulative
      // zoom, consistently at any depth).
      scale={scale}
      depth={depth}
      ctx={ctx}
      bandVars={bandVars}
      context={context}
    />
  );
}

/**
 * Fit-contains the nested space in its SOLVED slot rect. The `fit` (and
 * the centring translate) are exact and synchronous — computed from the
 * scene-solved slot px, no ResizeObserver — and drive both the DOM
 * transform AND the `scale` prop handed down to the nested
 * `<SpaceRenderer>`, so child LOD bands key on the true cumulative
 * on-screen scale from the first frame.
 */
function NestedSpaceFit({
  innerSpace,
  slotX,
  slotY,
  slotW,
  slotH,
  sibling,
  scale: parentScale,
  depth,
  ctx,
  bandVars,
  context,
}: {
  innerSpace: BoardSpace;
  /** The subspace slot rect in item-local px — solved from scene geometry
   *  (`subspaceSlotRect`), not DOM-measured. `slotW/H` drive the fit-contain
   *  centring; `slotX/Y` position the wrapper when it is a SIBLING of the widget
   *  (scene / light DOM). */
  slotX: number;
  slotY: number;
  slotW: number;
  slotH: number;
  /** Scene widget (light DOM): the wrapper is an absolutely-positioned SIBLING of
   *  the widget host inside the item wrapper, placed at `slot.{x,y,w,h}`. Custom
   *  tag widget (shadow slot): the wrapper is `inset-0` of the `[part=subspace]`
   *  slot it is projected into (its origin IS the slot), so only `slotW/H`. */
  sibling: boolean;
  /** The widget host's current scale (screen px per item-model px). */
  scale: number;
  depth: number;
  ctx: ItemRenderContext;
  bandVars: React.CSSProperties;
  /** Part of the non-interactive gravity-well context layer — keep the nested
   *  preview pointer-transparent (the wrapper's `pointer-events:none` is
   *  otherwise re-enabled by the inner content div below) and propagate the
   *  flag so deeper previews stay non-interactive too. */
  context: boolean;
}) {
  // Fit-contain the nested space in its solved slot — exact + synchronous, so
  // the preview paints correct on its FIRST frame. No ResizeObserver (the slot
  // px are known from the scene), so no fit=0 blink and no per-level measurement
  // lag on deep spaces.
  const fit = Math.min(slotW / innerSpace.width, slotH / innerSpace.height);
  const tx = (slotW - innerSpace.width * fit) / 2;
  const ty = (slotH - innerSpace.height * fit) / 2;
  const childScale = parentScale * fit;
  // The well (floor + grid) fills the WHOLE slot, not the fit-contain content
  // box — shrink-wrapped wells came out a different size on every container.
  // Memoised: the nested SpaceRenderer is memo'd, and a fresh rect object per
  // render would defeat that memo for the whole projected subtree on every
  // hover/selection re-render of the owning item.
  const floorRect = useMemo(
    () => nestedFloorRect(innerSpace, slotW, slotH),
    [innerSpace, slotW, slotH],
  );

  return (
    <div
      // Marks a projected nested-space overlay. Present ONLY when the owning
      // node's scale ≥ INNER_RENDER_SCALE (the M2.5 render gate) — so its
      // presence/absence is the observable proof the gate skips invisible
      // subtrees (see widget-render e2e).
      data-nested-space={depth}
      className={
        sibling
          ? "absolute pointer-events-none overflow-hidden"
          : "absolute inset-0 pointer-events-none overflow-hidden"
      }
      style={{
        // Scene/light-DOM: pin to the solved slot rect (sibling of the widget).
        // Shadow-slot: `inset-0` of the projected slot (no x/y/w/h override).
        ...(sibling ? { left: slotX, top: slotY, width: slotW, height: slotH } : null),
        opacity: "var(--arch-band-compact-reveal, 0)",
        ...bandVars,
      }}
    >
      <div
        className={context ? "absolute pointer-events-none" : "absolute pointer-events-auto"}
        style={{
          left: 0,
          top: 0,
          width: innerSpace.width,
          height: innerSpace.height,
          transformOrigin: "0 0",
          transform:
            `translate(${tx}px, ${ty}px) scale(${fit}) ` +
            `translate(${-innerSpace.originX}px, ${-innerSpace.originY}px)`,
        }}
      >
        <SpaceRenderer
          space={innerSpace}
          scale={childScale}
          depth={depth + 1}
          ctx={ctx}
          context={context}
          floorRect={floorRect}
        />
      </div>
    </div>
  );
}

function DiagnosticBadge({
  item,
  diagnostics,
  onOpen,
}: {
  item: BoardItem;
  diagnostics: readonly ItemDiagnostic[] | undefined;
  onOpen?: (item: BoardItem, diag: ItemDiagnostic) => void;
}) {
  // Hover popover. Opens only after a deliberate hover (OPEN_DELAY) so the
  // pointer merely passing over the badge while panning/aiming elsewhere doesn't
  // pop it. Closes after a shorter grace delay so the pointer can travel from
  // the badge into the popover (and between rows) without it snapping shut. Both
  // timers cleared on unmount to avoid a stray setState after teardown.
  const OPEN_DELAY = 400;
  const CLOSE_DELAY = 220;
  const [open, setOpen] = useState(false);
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearTimers = useCallback(() => {
    if (openTimer.current !== null) {
      clearTimeout(openTimer.current);
      openTimer.current = null;
    }
    if (closeTimer.current !== null) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);
  // Enter the badge: schedule open (unless already open). Enter the popover or
  // re-enter while open: just cancel any pending close.
  const onEnter = useCallback(() => {
    if (closeTimer.current !== null) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    if (open || openTimer.current !== null) return;
    openTimer.current = setTimeout(() => {
      openTimer.current = null;
      setOpen(true);
    }, OPEN_DELAY);
  }, [open]);
  const scheduleClose = useCallback(() => {
    // A pending open is abandoned when the pointer leaves before the delay.
    if (openTimer.current !== null) {
      clearTimeout(openTimer.current);
      openTimer.current = null;
    }
    if (closeTimer.current !== null) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY);
  }, []);
  useEffect(() => clearTimers, [clearTimers]);

  if (!diagnostics || diagnostics.length === 0) return null;
  const worst = diagnostics.reduce<ItemDiagnostic["severity"]>(
    (acc, d) => severityRank(d.severity) > severityRank(acc) ? d.severity : acc,
    "hint",
  );
  const size = Math.min(item.h * 0.22, 28);
  const colour =
    worst === "error"
      ? "bg-red-500 border-red-200 text-white"
      : worst === "warning" || worst === "info"
        ? "bg-amber-500 border-amber-200 text-slate-900"
        : "bg-slate-400 border-slate-200 text-slate-900";
  // Positioned in SPACE coords so it sits at the item's top-right corner,
  // escaping the item's `overflow-hidden` and rounded corners.
  return (
    <div
      className={
        "absolute rounded-full border-2 font-bold flex items-center justify-center shadow-lg cursor-pointer pointer-events-auto " +
        colour
      }
      onMouseEnter={onEnter}
      onMouseLeave={scheduleClose}
      style={{
        left: item.x + item.w - size * 0.55,
        top: item.y - size * 0.45,
        width: size,
        height: size,
        fontSize: size * 0.55,
        zIndex: open ? 6 : 3,
      }}
    >
      {diagnostics.length > 1 ? diagnostics.length : "!"}
      {open && (
        <IssuePopover
          item={item}
          diagnostics={diagnostics}
          onOpen={onOpen}
          onMouseEnter={onEnter}
          onMouseLeave={scheduleClose}
        />
      )}
    </div>
  );
}

function IssuePopover({
  item,
  diagnostics,
  onOpen,
  onMouseEnter,
  onMouseLeave,
}: {
  item: BoardItem;
  diagnostics: readonly ItemDiagnostic[];
  onOpen?: (item: BoardItem, diag: ItemDiagnostic) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}) {
  // Only when the popover actually OVERFLOWS do we keep the wheel for its own
  // scroll — stopping it reaching the board's NATIVE (passive:false) ancestor
  // listener, which fires before any React delegated handler (so a React onWheel
  // would be too late). When the content fits (not scrollable), let the wheel
  // fall through so the board pans normally. Never preventDefault — the
  // popover's overflow scroll is the browser default.
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const stop = (e: WheelEvent) => {
      if (el.scrollHeight > el.clientHeight) e.stopPropagation();
    };
    el.addEventListener("wheel", stop, { passive: false });
    return () => el.removeEventListener("wheel", stop);
  }, []);
  // Anchored to the badge (its parent is position:absolute at the badge), so the
  // popover positions relative to the badge. Width is fixed in SPACE coords; the
  // surrounding board transform scales it with zoom like every other node.
  return (
    <div
      ref={ref}
      className="absolute left-1/2 top-full mt-1 -translate-x-1/2 rounded-md border border-slate-700 bg-slate-900/(--tint-solid) p-1 text-left shadow-2xl pointer-events-auto"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{ width: 280, maxHeight: 220, overflowY: "auto", zIndex: 7 }}
    >
      {diagnostics.map((d, i) => {
        const clickable = Boolean(onOpen);
        return (
          <Button
            key={i}
            variant="ghost"
            size="xs"
            disabled={!clickable}
            onClick={clickable ? () => onOpen!(item, d) : undefined}
            className={
              // Rows wrap to several lines, so the kit's fixed height/centering
              // is overridden back to a top-aligned auto-height row.
              "h-auto w-full items-start justify-start gap-2 py-1.5 text-left leading-snug " +
              (clickable
                ? "cursor-pointer"
                : "cursor-default text-slate-300 disabled:opacity-100")
            }
            title={clickable ? "Open in editor at this location" : undefined}
          >
            <span
              className={"mt-0.5 h-2 w-2 shrink-0 rounded-full " + severityDot(d.severity)}
            />
            <span className="min-w-0 break-words font-medium normal-case">
              {d.message}
            </span>
          </Button>
        );
      })}
    </div>
  );
}

function severityRank(s: ItemDiagnostic["severity"]): number {
  return s === "error" ? 3 : s === "warning" ? 2 : s === "info" ? 1 : 0;
}

function severityDot(s: ItemDiagnostic["severity"]): string {
  return s === "error"
    ? "bg-red-400"
    : s === "warning"
      ? "bg-amber-400"
      : s === "info"
        ? "bg-sky-400"
        : "bg-slate-400";
}

export function InternalCallsLayer({
  item,
  activeProcessId,
  activeStepIndex = null,
  edgeDecoMap,
  onSelect,
}: {
  item: BoardItem;
  activeProcessId: string;
  activeStepIndex?: number | null;
  edgeDecoMap?: EdgeDecoMap;
  onSelect: () => void;
}) {
  const calls = (item.internalCalls ?? []).filter(
    (c) => c.processId === activeProcessId,
  );
  // An internal call is a CONNECTION whose endpoints both collapse into this
  // item at this level (it's drawn as an edge deeper in). Wire it to the same
  // stores as edges/ports so it's a first-class unit: hover/click it → the whole
  // connection lights/selects across levels; and it lights when any other piece
  // (e.g. its deeper edge) is hovered/selected.
  const connIds = connectionIdsFromDetails(calls);
  const connHover = useConnectionHoverStore();
  const connSel = useConnectionSelectionStore();
  const connHovered = useConnectionHovered(connIds);
  const connSelected = useConnectionSelected(connIds);
  if (calls.length === 0) return null;
  const emph = connHovered || connSelected;
  const steps = [
    ...new Set(
      calls
        .map((c) => c.stepIndex)
        .filter((i): i is number => typeof i === "number"),
    ),
  ].sort((a, b) => a - b);
  // Same walker join as edges/ports: a badge index absent from a PRESENT map
  // is a step on an unselected branch — rendered quiet, not full-bright.
  const walkerActive = edgeDecoMap !== undefined;
  // Position below the header area.
  const top = item.h * 0.16;
  const padding = item.w * 0.04;
  const badgeSize = Math.min(item.h * 0.22, 38);
  return (
    <div
      className="absolute pointer-events-auto flex flex-wrap gap-1.5"
      data-connection-id={connIds.join(" ")}
      style={{
        left: padding,
        right: padding,
        top,
        maxHeight: item.h * 0.7,
      }}
      onMouseEnter={() => connHover.setHovered(connIds)}
      onMouseLeave={() => connHover.setHovered(null)}
      onClick={(e) => {
        e.stopPropagation();
        connSel.setSelected(connIds);
        onSelect();
      }}
      title={calls
        .map((c) => `${c.fromPath} → ${c.toPath}`)
        .join("\n")}
    >
      {steps.map((n) => {
        const deco = edgeDecoMap?.get(n);
        const offPath = walkerActive && !deco;
        const isCurrent = activeStepIndex !== null && n === activeStepIndex;
        const dimmed = activeStepIndex !== null && !isCurrent;
        // Sequential run number when the walker resolved it; `·` off-path;
        // canonical+1 only in walker-less contexts (same rule as edge chips).
        const label = deco ? chipLabelFor(deco) : walkerActive ? "·" : String(n + 1);
        return (
          <span
            key={n}
            data-offpath={offPath ? "true" : undefined}
            className={
              "rounded-full text-white font-semibold flex items-center justify-center shadow-md border-2 " +
              (emph
                ? "bg-sky-400 border-sky-200 ring-2 ring-sky-300"
                : offPath
                  ? "bg-slate-600 border-white/40 border-dashed"
                  : isCurrent
                    ? "bg-sky-600 border-white ring-2 ring-sky-300"
                    : "bg-sky-500 border-white/80")
            }
            style={{
              width: badgeSize,
              height: badgeSize,
              fontSize: badgeSize * 0.45,
              opacity: offPath ? (dimmed ? 0.2 : 0.6) : dimmed ? 0.35 : 1,
            }}
          >
            {label}
          </span>
        );
      })}
    </div>
  );
}

interface EdgesLayerProps {
  readonly edges: readonly BoardEdge[];
  readonly items: ReadonlyMap<string, BoardItem>;
  readonly ctx: ItemRenderContext;
  readonly spaceW: number;
  readonly spaceH: number;
  readonly viewBounds?: { x1: number; y1: number; x2: number; y2: number };
  readonly scale: number;
  /**
   * Suffix appended to every `<marker>` id in this layer's `<defs>` and
   * to every `url(#...)` reference. SVG marker IDs are document-global,
   * so nested SpaceRenderers sharing the same hardcoded ids
   * (`arrow-default` etc.) collide — `url(#arrow-default)` from an
   * inner path can resolve to an outer SVG's marker, which (with
   * `markerUnits="userSpaceOnUse"`) then draws in outer coords and
   * lands off-screen. Per-space suffix keeps each layer self-contained.
   */
  readonly idSuffix?: string;
}

interface PortsLayerProps {
  readonly items: readonly BoardItem[];
  readonly ctx: ItemRenderContext;
  readonly spaceW: number;
  readonly spaceH: number;
  /** Space frame's origin in item-coord space. Can be negative when the
   *  solver placed items with negative coords. Port x-positions reference
   *  this so they sit just outside the actual frame walls, not the (0,0)
   *  wall of a mentally-repositioned space. */
  readonly originX: number;
  readonly originY: number;
}

/**
 * Renders cross-level connection ports as "sidenodes" on the space's walls.
 * Each port aggregates one or more underlying edges; hover reveals the
 * individual service-level details. Placement is internal-only — flow wall by
 * direction, centred on the owning child (`resolvePortPlacements`); the outer
 * layout has no say in where a pill sits.
 */
export function PortsLayer({ items, ctx, spaceW, spaceH, originX, originY }: PortsLayerProps) {
  // Connection-as-unit: hover any piece → the whole connection lights across
  // levels; click any piece → the whole connection is SELECTED (persistent).
  const hoveredConns = useHoveredConnections();
  const connHover = useConnectionHoverStore();
  const selectedConns = useSelectedConnections();
  const connSel = useConnectionSelectionStore();
  const lifecyclePaint = useLifecyclePaint();
  // A port is a segment of its owning item's connection, so it recedes with
  // that item — the item's veil can't reach it (the nub is drawn on the
  // enclosing space's wall, outside the item's box).
  const boardFocus = useBoardFocus();

  const positions = resolvePortPlacements(items, { spaceW, spaceH, originX, originY });
  if (positions.length === 0) return null;

  const sel = ctx.selectedId;
  return (
    <>
      {/* Lines from each item to its ports (SVG for curves). */}
      <svg
        className="absolute inset-0 pointer-events-none"
        viewBox={`0 0 ${spaceW} ${spaceH}`}
        preserveAspectRatio="none"
        width={spaceW}
        height={spaceH}
        style={{ overflow: "visible" }}
      >
        <defs>
          {/* Fixed interaction palette PLUS every distinct interface-style colour
              the ports carry, so a widget.*-coloured segment's arrowhead matches
              its stroke (segments are styled like edges now). */}
          {[
            ...new Set([
              EDGE_DEFAULT, EDGE_SELECTED, EDGE_OUT, EDGE_IN, EDGE_BLAST,
              ...positions.map((p) => p.entry.port.widgetStyle?.color).filter((c): c is string => !!c),
            ]),
          ].map((c) => (
            <ArrowheadMarker key={c} id={`port-${arrowMarkerId(c)}`} color={c} />
          ))}
        </defs>
        {positions.map(({ entry, side, px, py, ix, iy }) => {
          const portSel = ctx.selectedEdgeId === entry.port.id;
          const itemSel = sel !== null && sel === entry.item?.id;
          // A mutual port satisfies both, and `resolveEdgeStyle` resolves out
          // first, so it takes the amber. One line can only be one colour; the
          // ⇄ on the pill is what carries the mutuality.
          const isOut = itemSel && entry.port.direction !== "in";
          const isIn = itemSel && entry.port.direction !== "out";
          const processActive = !!ctx.activeProcessId;
          const activeDetails = processActive
            ? entry.port.details.filter((d) => d.processId === ctx.activeProcessId)
            : [];
          const inProcess = processActive ? activeDetails.length > 0 : true;
          // Same off-path rule as edges: the port's steps join the walker's
          // selected walk by canonical stepIndex; none resolving means every
          // crossing here belongs to an unselected branch. Map PRESENCE (not
          // size) is the walker signal — an empty map is a real walk whose
          // selected path has zero call steps.
          const offPath =
            inProcess &&
            ctx.edgeDecoMap !== undefined &&
            !activeDetails.some(
              (d) => typeof d.stepIndex === "number" && ctx.edgeDecoMap!.has(d.stepIndex),
            );
          const inBlast =
            (ctx.highlighted.size > 0 &&
              ((entry.item != null && ctx.highlighted.has(entry.item.id)) ||
                // Self/ghost port has no owning item — light it when the connection's
                // deep endpoint (its peer or target) is the highlighted node.
                entry.port.details.some((d) => ctx.highlighted.has(d.fromId) || ctx.highlighted.has(d.toId)))) ||
            // Node-select: light when the selected node is this connection's deep endpoint.
            (ctx.selectedId != null &&
              entry.port.details.some((d) => d.fromId === ctx.selectedId || d.toId === ctx.selectedId));
          const isCurrentStep =
            ctx.activeStepIndex !== null &&
            entry.port.details.some(
              (d) => d.processId === ctx.activeProcessId && d.stepIndex === ctx.activeStepIndex,
            );
          const { stroke, width, opacity, dashed } = resolveEdgeStyle({
            edgeSel: portSel,
            isOut,
            isIn,
            inBlast,
            inProcess,
            offPath,
            isCurrentStep,
            stepScrubActive: ctx.activeStepIndex !== null,
            processActive,
            widgetStyle: edgeLifecycleStyle(
              lifecyclePaint,
              entry.port.details[0]?.fromPath,
              entry.port.details[0]?.toPath,
              entry.port.widgetStyle,
            ),
            ground: !!boardFocus && !!entry.item && boardFocus.role(entry.item) === "ground",
          });
          const connIds = connectionIdsFromDetails(entry.port.details);
          const connHovered = connIds.some((id) => hoveredConns.has(id));
          const connSelected = connIds.some((id) => selectedConns.has(id));
          const emph = connHovered || connSelected;
          // Port arrow should point in the edge's semantic direction:
          //   "out" port → item sends → arrow points from item toward pill.
          //   "in"  port → item receives → arrow points from pill toward item.
          // We draw the path from the SOURCE end, so the arrow at path end is
          // at the target end automatically (orient=auto).
          const { d } = portConnectorPath(side, entry.port.direction, ix, iy, px, py);
          const portMarker = `port-${arrowMarkerId(connSelected ? EDGE_SELECTED : stroke)}`;
          return (
            <g
              key={`${entry.item?.id ?? entry.port.id}|${entry.port.id}|line`}
              style={{ pointerEvents: "auto", cursor: "pointer" }}
              onMouseEnter={() => connHover.setHovered(connIds)}
              onMouseLeave={() => connHover.setHovered(null)}
              onClick={(e) => {
                e.stopPropagation();
                // Boundary stub: the line is the same click surface as the
                // pill — clicking reveals the absent peer (VIEWS §7).
                if (entry.port.boundary && ctx.onRevealBoundary) {
                  ctx.onRevealBoundary(entry.port.peerId);
                  return;
                }
                connSel.setSelected(connIds);
                ctx.onSelectEdge(entry.port.id);
              }}
              onContextMenu={
                ctx.onEdgeContextMenu && !entry.port.boundary
                  ? (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      ctx.onEdgeContextMenu!(entry.port.id, e.clientX, e.clientY);
                    }
                  : undefined
              }
            >
              {/* Wide invisible hit-target so the thin segment is selectable. */}
              <path d={d} fill="none" stroke="transparent" strokeWidth={14} vectorEffect="non-scaling-stroke" />
              <path
                data-connection-id={connIds.join(" ")}
                d={d}
                fill="none"
                stroke={connSelected ? EDGE_SELECTED : stroke}
                strokeOpacity={emph ? 1 : opacity}
                strokeWidth={emph ? width * 1.8 : width}
                {...(dashed ? { strokeDasharray: "6 4" } : {})}
                vectorEffect="non-scaling-stroke"
                markerEnd={markerUrl(portMarker)}
                {...(entry.port.direction === "inout"
                  ? // Mutual peer: one stroke, a head at each end. The marker
                    // is declared `orient="auto-start-reverse"`, so the same
                    // def flips itself at the start — no second path, no
                    // doubled stroke weight.
                    { markerStart: markerUrl(portMarker) }
                  : {})}
              />
            </g>
          );
        })}
      </svg>

      {/* Sidenode pills on the wall. DOM so they're selectable/clickable. */}
      {positions.map(({ entry, side, px, py }) => {
        const portSel = ctx.selectedEdgeId === entry.port.id;
        const itemSel = sel !== null && sel === entry.item?.id;
        // Matches the connector's own out/in resolution above, so a selected
        // node's pill and the line reaching it are never two different colours.
        const isOut = entry.port.direction !== "in";
        const connIds = connectionIdsFromDetails(entry.port.details);
        const connHovered = connIds.some((id) => hoveredConns.has(id));
        const connSelected = connIds.some((id) => selectedConns.has(id));
        const inBlast =
          (ctx.highlighted.size > 0 &&
            entry.port.details.some((d) => ctx.highlighted.has(d.fromId) || ctx.highlighted.has(d.toId))) ||
          (ctx.selectedId != null &&
            entry.port.details.some((d) => d.fromId === ctx.selectedId || d.toId === ctx.selectedId));
        const pillAccent =
          portSel || connSelected
            ? "border-sky-400 bg-sky-500/25 text-sky-100"
            : inBlast
              ? "border-yellow-300 bg-yellow-500/15 text-yellow-100"
              : itemSel
                ? isOut
                  ? "border-orange-400 bg-orange-500/20 text-orange-100"
                  : "border-green-400 bg-green-500/20 text-green-100"
                : "border-slate-500/(--tint-strong) bg-slate-800/90 text-slate-200 hover:border-slate-300";
        return (
          <button
            type="button"
            key={`${entry.item?.id ?? entry.port.id}|${entry.port.id}|pill`}
            onMouseEnter={() => connHover.setHovered(connIds)}
            onMouseLeave={() => connHover.setHovered(null)}
            onClick={(e) => {
              e.stopPropagation();
              // Boundary stub: clicking REVEALS the absent peer (VIEWS §7)
              // instead of selecting a connection to a node that isn't on
              // the board.
              if (entry.port.boundary && ctx.onRevealBoundary) {
                ctx.onRevealBoundary(entry.port.peerId);
                return;
              }
              connSel.setSelected(connIds);
              ctx.onSelectEdge(entry.port.id);
            }}
            onContextMenu={
              ctx.onEdgeContextMenu && !entry.port.boundary
                ? (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    ctx.onEdgeContextMenu!(entry.port.id, e.clientX, e.clientY);
                  }
                : undefined
            }
            title={entry.port.details
              .map((d) => `${d.fromPath} → ${d.toPath}`)
              .join("\n")}
            data-hud
            data-port-side={side}
            data-connection-id={connectionIdsFromDetails(entry.port.details).join(" ")}
            className={
              // Ghost node: a plain rounded-rect endpoint — same visual weight as
              // a regular node, styled from the shared edge palette (below).
              "absolute flex items-center gap-1.5 rounded-lg border overflow-hidden " +
              FOCUS_RING + " " +
              pillAccent
            }
            style={{
              // Hug the wall (outer edge AT the wall) + size to content, so a
              // ghost is a compact tag near the edge, not a fixed slab covering
              // the nested nodes. Right-wall ghosts anchor their right edge via
              // translateX(-100%) so they extend inward, not off the wall.
              // Centre the pill on (px,py) against its wall (hug-the-wall
              // translate on the far side). Pure geometry — see portPillBox.
              ...portPillBox(side, px, py),
              // Fixed width (not max-content): the connector docks at the pill's
              // inner edge, so a uniform width keeps every arrowhead touching its
              // chip and lines the chips into a column (or a row, on T/B walls).
              width: PORT_W,
              height: PORT_H,
              padding: "0 9px",
              fontSize: 12,
              fontWeight: 600,
              // No backdrop-filter: a blur here re-samples whatever moves
              // behind EVERY port pill on EVERY gesture frame (compositor
              // cost that scales with pill count); the pill palette's
              // near-opaque background reads the same without it.
              // Interface-styled connection → the ghost carries the connection's
              // colour on its border (unless an interaction state overrides).
              ...(entry.port.widgetStyle?.color && !portSel && !itemSel
                ? { borderColor: entry.port.widgetStyle.color }
                : {}),
              ...(entry.port.widgetStyle?.dashed ? { borderStyle: "dashed" } : {}),
              // Whole-connection hover/selection: this ghost is one piece of a
              // hovered or SELECTED connection (possibly from another level) → ring
              // it. Selection persists; hover is transient.
              ...(connHovered || connSelected ? { boxShadow: "0 0 0 3px rgba(56,189,248,0.6)", zIndex: 20 } : {}),
            }}
          >
            <span
              className={
                entry.port.direction === "inout"
                  ? "text-slate-300"
                  : isOut
                    ? "text-orange-300"
                    : "text-green-300"
              }
              style={{ fontSize: 13 }}
              aria-hidden
            >
              {portGlyph(side, entry.port.direction)}
            </span>
            <span className="truncate flex-1 text-left">
              {entry.port.peerLabel}
            </span>
            {entry.port.details.length > 1 && (
              <span className="rounded-full bg-slate-700 text-slate-300 px-1.5 text-xs">
                ×{entry.port.details.length}
              </span>
            )}
          </button>
        );
      })}
    </>
  );
}

// Direction-aware edge colours. Defaults are muted; when a module is selected,
// its outgoing edges go amber and incoming go emerald for instant direction
// read.
// JetBrains Islands Dark edge accents. Concrete hex (not theme vars) because
// the stroke is applied as an SVG presentation attribute, which can't resolve
// CSS var(); arrowheads match via one explicitly-filled <marker> per colour
// (see ArrowheadMarker — Safari has no context-stroke).
const EDGE_DEFAULT = "#6f737a"; // muted grey
const EDGE_SELECTED = "#3574f0"; // accent blue
const EDGE_OUT = "#d6a052"; // amber — "leaving"
const EDGE_IN = "#5fad65"; // green — "arriving"
const EDGE_BLAST = "#f5c85c"; // yellow — within blast radius

type Side = SharedSide;
type Rect = SharedRect;
type Anchor = SharedAnchor;

// =============================================================================
// EdgePath — presentational, per-edge drawing
// =============================================================================
//
// Pure draw: the two stacked paths (wide invisible hit-target + thin visible
// stroke), the step badge, the process decoration, and any widget/badge
// overlay. All routing/styling is decided upstream in planRoutes +
// resolveEdgeStyle and arrives as plain props, so this component is
// memoizable — selecting one edge re-renders only the edges whose props
// actually changed, not the whole layer.
interface EdgePathProps {
  readonly edgeId: string;
  readonly edge: BoardEdge;
  readonly d: string;
  readonly stroke: string;
  readonly opacity: number;
  readonly width: number;
  readonly dashed: boolean;
  /** Full marker id (sans suffix) — colour-keyed (`arrow-c-*`) or diff
   *  (`arrow-diff-*`), always matching this edge's rendered stroke. */
  readonly marker: string;
  readonly sfx: string;
  readonly diffClass?: string;
  /** Step chip text: sequential run numbers ("3" / "3,7"), `·` (off-path),
   *  `↩` (compensation-only). Null → no process chip. */
  readonly stepChipLabel: string | null;
  /** True when the edge's steps exist in the process but only on branches the
   *  walker did NOT select — rendered present-but-off-path. */
  readonly offPath: boolean;
  readonly isCurrentStep: boolean;
  readonly activeStepIndex: number | null;
  readonly mx: number;
  readonly my: number;
  readonly deco?: EdgeDeco;
  readonly scale: number;
  readonly onSelectEdge: (id: string) => void;
  readonly onEdgeContextMenu?: (edgeId: string, x: number, y: number) => void;
}

const EdgePath = memo(function EdgePath({
  edgeId,
  edge,
  d,
  stroke,
  opacity,
  width,
  dashed,
  marker,
  sfx,
  diffClass,
  stepChipLabel,
  offPath,
  isCurrentStep,
  activeStepIndex,
  mx,
  my,
  deco,
  scale,
  onSelectEdge,
  onEdgeContextMenu,
}: EdgePathProps) {
  // Connection-as-unit: this edge is a piece of one or more connections. Hover
  // it → light the whole chain (all its segments across every open level); click
  // it → SELECT the whole connection (persistent). It lights/selects when any
  // OTHER piece of its connection is. Boolean snapshots (not the raw set) so this
  // memoised edge re-renders only when ITS state flips.
  const connHover = useConnectionHoverStore();
  const connSel = useConnectionSelectionStore();
  const connIds = useMemo(() => connectionIdsFromDetails(edge.details), [edge]);
  const connHovered = useConnectionHovered(connIds);
  const connSelected = useConnectionSelected(connIds);
  const emph = connHovered || connSelected;
  const onClick = useCallback(
    (evt: React.MouseEvent) => {
      evt.stopPropagation();
      connSel.setSelected(connIds);
      onSelectEdge(edgeId);
    },
    [connSel, connIds, onSelectEdge, edgeId],
  );
  const onCtx = useCallback(
    (evt: React.MouseEvent) => {
      if (!onEdgeContextMenu) return;
      evt.preventDefault();
      evt.stopPropagation();
      onEdgeContextMenu(edgeId, evt.clientX, evt.clientY);
    },
    [onEdgeContextMenu, edgeId],
  );
  // Track whether WE set the current hover, so an unmount while hovered (LOD cull
  // / pan off-screen — React fires no onMouseLeave then) still clears it instead
  // of leaving the connection's other pieces stuck-lit.
  const setByMe = useRef(false);
  const onEnter = useCallback(() => {
    setByMe.current = true;
    connHover.setHovered(connIds);
  }, [connHover, connIds]);
  const onLeave = useCallback(() => {
    setByMe.current = false;
    connHover.setHovered(null);
  }, [connHover]);
  useEffect(() => () => {
    if (setByMe.current) connHover.setHovered(null);
  }, [connHover]);
  return (
    <g style={{ pointerEvents: "auto", cursor: "pointer" }} onMouseEnter={onEnter} onMouseLeave={onLeave} onContextMenu={onCtx}>
      {/* Wide invisible hit-target so thin curves are easy to click. */}
      <path
        d={d}
        fill="none"
        stroke="transparent"
        strokeWidth={16}
        vectorEffect="non-scaling-stroke"
        onClick={onClick}
      />
      <path
        d={d}
        fill="none"
        stroke={connSelected ? EDGE_SELECTED : stroke}
        strokeOpacity={emph ? 1 : opacity}
        strokeWidth={emph ? width * 1.8 : width}
        strokeDasharray={dashed ? "6 5" : undefined}
        vectorEffect="non-scaling-stroke"
        markerEnd={markerUrl(connSelected ? `${arrowMarkerId(EDGE_SELECTED)}${sfx}` : `${marker}${sfx}`)}
        onClick={onClick}
        className={diffClass}
      />
      {stepChipLabel !== null && (
        <StepChip
          label={stepChipLabel}
          offPath={offPath}
          compensation={deco?.modifiers.compensation === true}
          isCurrentStep={isCurrentStep}
          activeStepIndex={activeStepIndex}
          mx={mx}
          my={my}
          onClick={onClick}
        />
      )}
      {deco && <EdgeDecoration deco={deco} edgeMid={{ x: mx, y: my }} />}
      {(edge.widgetTag || edge.widgetTemplate) && stepChipLabel === null && (
        <EdgeWidgetOverlay edge={edge} pathD={d} midX={mx} midY={my} scale={scale} />
      )}
      {!edge.widgetTag &&
        !edge.widgetTemplate &&
        stepChipLabel === null &&
        (() => {
          const badge = edgeBadgeProps(edge.widgetProps);
          if (!badge) return null;
          return (
            <EdgeDefaultBadge
              icon={badge.icon}
              text={badge.text}
              iconColor={badge.iconColor}
              textColor={badge.textColor}
              strokeColor={stroke}
              midX={mx}
              midY={my}
            />
          );
        })()}
    </g>
  );
});

export function EdgesLayer({ edges, items, ctx, spaceW, spaceH, viewBounds, scale, idSuffix }: EdgesLayerProps) {
  const sfx = idSuffix ? `-${idSuffix}` : "";
  const lifecyclePaint = useLifecyclePaint();
  // View `focus`: an edge is figure only when BOTH endpoints are. Live never
  // runs the `applyFocus` fold (it would rebuild a BoardSpace for a paint
  // change), so the roles are resolved here off the same context the items use.
  const boardFocus = useBoardFocus();
  const isGroundEdge = (from: string, to: string): boolean => {
    if (!boardFocus) return false;
    // An endpoint this space doesn't hold can't be classified — leave the wire
    // alone rather than guess it into the ground.
    const figure = (id: string) => {
      const it = items.get(id);
      return !it || boardFocus.role(it) === "figure";
    };
    return !(figure(from) && figure(to));
  };
  const sel = ctx.selectedId;
  // Persist previous side choices across renders so chooseSides can apply
  // hysteresis — otherwise dragging items causes near-tied side pairs to
  // flip on every pixel of motion and the arrows visibly vibrate.
  const prevSidesRef = useRef<Map<string, { fromSide: Side; toSide: Side }>>(new Map());
  // Routes are pure geometry — independent of viewport. Recompute only when
  // edges/items change, NOT on every pan (viewBounds change). planRoutes
  // allocates several Maps per space; running it per cell-crossing is the
  // main remaining GC churn on full-board pans.
  const routes = useMemo(() => {
    const r = planRoutes(edges, items, prevSidesRef.current);
    const nextSides = new Map<string, { fromSide: Side; toSide: Side }>();
    for (const [id, rt] of r) {
      nextSides.set(id, { fromSide: rt.fromSide, toSide: rt.toSide });
    }
    prevSidesRef.current = nextSides;
    return r;
  }, [edges, items]);
  // Every stroke an edge can take, regardless of interaction state: the
  // fixed palette plus each edge's widget/aspect colour. resolveEdgeStyle
  // only ever returns one of these, so pre-declaring a marker per colour
  // covers all states without re-resolving styles up-front.
  const markerColors = useMemo(() => {
    const set = new Set<string>([EDGE_DEFAULT, EDGE_SELECTED, EDGE_OUT, EDGE_IN, EDGE_BLAST]);
    for (const e of edges) {
      if (e.widgetStyle?.color) set.add(e.widgetStyle.color);
    }
    return [...set];
  }, [edges]);
  // Process join, hoisted per edge id: walker decorations attach by the step's
  // dense canonical index — the one numbering the graph's edge details and the
  // walk share (the emitter owns it). An index missing from the map is a step
  // on an UNSELECTED branch: the edge is on the process but off the selected
  // path. Path-string joins are impossible here (details carry resolved module
  // pairs; the walk carries written caller → interface paths). Memoized so the
  // arrays/objects handed to the memoised EdgePath stay referentially stable
  // across pan/zoom renders (viewBounds/scale churn) — only a real walk change
  // (edgeDecoMap identity) or an edge-set change rebuilds them.
  const processByEdge = useMemo(() => {
    if (!ctx.activeProcessId) return undefined;
    // A walker map PRESENT (even empty — a selected path with zero call steps)
    // means the walk is authoritative; absent means no IR walked → fallback.
    const walkerActive = ctx.edgeDecoMap !== undefined;
    const map = new Map<
      string,
      {
        detailIndices: readonly number[];
        chipLabel: string;
        deco?: EdgeDeco;
        offPath: boolean;
      }
    >();
    for (const e of edges) {
      const detailIndices = [
        ...new Set(
          e.details
            .filter((d) => d.processId === ctx.activeProcessId)
            .map((d) => d.stepIndex)
            .filter((i): i is number => typeof i === "number"),
        ),
      ].sort((a, b) => a - b);
      if (detailIndices.length === 0) continue;
      const deco = ctx.edgeDecoMap
        ? aggregateEdgeDeco(detailIndices.map((i) => ctx.edgeDecoMap!.get(i)))
        : undefined;
      // Chip text: the walker's SEQUENTIAL run numbers when decorated (dense
      // 1..K positions of the selected walk — canonical indices are internal
      // identity only); `·` for an off-path edge (no position in the selected
      // run); raw canonical numbers only in walker-less contexts (no IR).
      const chipLabel = deco
        ? chipLabelFor(deco)
        : walkerActive
          ? "·"
          : detailIndices
              .slice(0, 3)
              .map((i) => i + 1)
              .join(",") + (detailIndices.length > 3 ? ",…" : "");
      map.set(e.id, {
        detailIndices,
        chipLabel,
        deco,
        offPath: walkerActive && !deco,
      });
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edges, ctx.activeProcessId, ctx.edgeDecoMap]);
  return (
    <svg
      className="absolute inset-0"
      viewBox={`0 0 ${spaceW} ${spaceH}`}
      preserveAspectRatio="none"
      width={spaceW}
      height={spaceH}
      style={{ overflow: "visible", pointerEvents: "none" }}
    >
      <defs>
        {/* One marker per distinct stroke colour in this layer (fixed
            interaction palette + any widget/aspect colours present in the
            edge set), so every path can reference an arrowhead that
            matches its line. Explicit fills because Safari doesn't
            support context-stroke. Diff edges take their stroke from the
            .diff-edge-* CSS classes, so their arrowheads use the same
            theme variables. */}
        {markerColors.map((c) => (
          <ArrowheadMarker key={c} id={`${arrowMarkerId(c)}${sfx}`} color={c} />
        ))}
        <ArrowheadMarker id={`arrow-diff-added${sfx}`} color="rgb(var(--success))" />
        <ArrowheadMarker id={`arrow-diff-removed${sfx}`} color="rgb(var(--danger))" />
        <ArrowheadMarker id={`arrow-diff-modified${sfx}`} color="rgb(var(--warning))" />
      </defs>
      {edges.map((e) => {
        const route = routes.get(e.id);
        if (!route) return null;
        const fromItem = items.get(e.from);
        const toItem = items.get(e.to);
        if (!fromItem || !toItem) return null;
        // Culling: skip edges whose both endpoints are fully outside viewBounds.
        if (viewBounds && !rectInView(fromItem, viewBounds) && !rectInView(toItem, viewBounds)) {
          return null;
        }
        // LOD: skip edges that project to < ~2 screen pixels.
        const chordLen = Math.hypot(
          route.toAnchor.x - route.fromAnchor.x,
          route.toAnchor.y - route.fromAnchor.y,
        );
        if (chordLen * scale < 2) return null;
        const d = route.d;
        const edgeSel = ctx.selectedEdgeId === e.id;
        const isOut = sel !== null && sel === e.from;
        const isIn = sel !== null && sel === e.to;
        // Process view: this edge's slice of the hoisted process join.
        const proc = processByEdge?.get(e.id);
        const inProcess = ctx.activeProcessId ? proc !== undefined : true;
        const deco = proc?.deco;
        const offPath = proc?.offPath ?? false;
        const stepChipLabel = proc?.chipLabel ?? null;
        const isCurrentStep =
          ctx.activeStepIndex !== null &&
          proc !== undefined &&
          proc.detailIndices.includes(ctx.activeStepIndex);

        // Edge highlights from blast radius OR when its target interface
        // explicitly carries the legend aspect. Member NODE highlight
        // (ctx.memberHighlighted) deliberately does NOT light edges — a
        // member's unrelated connections stay quiet.
        const isMemberEdge =
          (ctx.memberInterfaces?.size ?? 0) > 0 &&
          e.details.some((d) => d.toInterfaceId && ctx.memberInterfaces!.has(d.toInterfaceId));
        const inBlast =
          (ctx.highlighted.size > 0 &&
            (ctx.highlighted.has(e.from) ||
              ctx.highlighted.has(e.to) ||
              e.details.some((d) => ctx.highlighted.has(d.fromId) || ctx.highlighted.has(d.toId)))) ||
          // Node-SELECT lights every segment of the selected node's connections,
          // across levels — matched on the deep endpoints so an outer segment
          // whose real endpoint is nested still lights.
          (ctx.selectedId != null &&
            (e.from === ctx.selectedId ||
              e.to === ctx.selectedId ||
              e.details.some((d) => d.fromId === ctx.selectedId || d.toId === ctx.selectedId))) ||
          isMemberEdge;
        const { stroke, width, opacity, dashed } = resolveEdgeStyle({
          edgeSel,
          isOut,
          isIn,
          inBlast,
          inProcess,
          offPath,
          isCurrentStep,
          stepScrubActive: ctx.activeStepIndex !== null,
          processActive: !!ctx.activeProcessId,
          // An edge exists only while both endpoints do, so its resting style
          // folds in the harsher endpoint state before interaction overrides.
          widgetStyle: edgeLifecycleStyle(
            lifecyclePaint,
            e.details[0]?.fromPath,
            e.details[0]?.toPath,
            e.widgetStyle,
          ),
          isAspect: e.isAspect === true,
          ground: isGroundEdge(e.from, e.to),
        });
        const diffClass =
          e.diffStatus === "added" ||
          e.diffStatus === "removed" ||
          e.diffStatus === "modified"
            ? `diff-edge-${e.diffStatus}`
            : undefined;
        // Arrowhead must match the line. Diff edges stroke via CSS class
        // (theme vars), so they use the matching diff marker; everything
        // else keys the marker off the resolved stroke colour.
        const marker = diffClass ? `arrow-diff-${e.diffStatus}` : arrowMarkerId(stroke);
        // On-path midpoint (arc length of the drawn `d`) — sits on the line's
        // middle for curved and axis-aligned routes alike, where the chord
        // midpoint would float off into empty space.
        const mx = route.mid.x;
        const my = route.mid.y;
        return (
          <EdgePath
            key={e.id}
            edgeId={e.id}
            edge={e}
            d={d}
            stroke={stroke}
            opacity={opacity}
            width={width}
            dashed={dashed}
            marker={marker}
            sfx={sfx}
            diffClass={diffClass}
            stepChipLabel={stepChipLabel}
            offPath={offPath}
            isCurrentStep={isCurrentStep}
            activeStepIndex={ctx.activeStepIndex}
            mx={mx}
            my={my}
            deco={deco}
            scale={scale}
            onSelectEdge={ctx.onSelectEdge}
            onEdgeContextMenu={ctx.onEdgeContextMenu}
          />
        );
      })}
      <BoardOverlayLayers edges={edges} routes={routes} nodes={items} />
    </svg>
  );
}

// =============================================================================
// Edge widget overlay
// =============================================================================
//
// Renders a custom-element (widgetTag) or inline-HTML template
// (widgetTemplate) at the edge's midpoint. Wrapped in <foreignObject>
// so HTML can be hosted inside the SVG canvas. The overlay is sized
// generously enough to fit a typical badge / status pill; widget
// authors can layout-shift inside via internal styles. The DOM
// property `archEdge` carries structured edge data so widgets can
// react to context (from/to module names, computed path, call
// details for drill-down).

const EDGE_OVERLAY_W = 160;
const EDGE_OVERLAY_H = 56;

function EdgeWidgetOverlay({
  edge,
  pathD,
  midX,
  midY,
  scale,
}: {
  edge: BoardEdge;
  pathD: string;
  midX: number;
  midY: number;
  scale: number;
}) {
  return (
    <foreignObject
      x={midX - EDGE_OVERLAY_W / 2}
      y={midY - EDGE_OVERLAY_H / 2}
      width={EDGE_OVERLAY_W}
      height={EDGE_OVERLAY_H}
      style={{ overflow: "visible", pointerEvents: "auto" }}
    >
      {/* Browsers infer the XHTML namespace for foreignObject children;
          React doesn't expose xmlns on HTMLDivElement so we rely on
          that inference. */}
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          pointerEvents: "auto",
          ...bandCssVars(scale),
        }}
      >
        {edge.widgetTag ? (
          <EdgeTagWidget
            edge={edge}
            tag={edge.widgetTag}
            pathD={pathD}
            scale={scale}
          />
        ) : edge.widgetTemplate ? (
          <EdgeTemplateWidget
            edge={edge}
            template={edge.widgetTemplate}
            scale={scale}
          />
        ) : null}
      </div>
    </foreignObject>
  );
}

function EdgeDefaultBadge({
  icon,
  text,
  iconColor,
  textColor,
  strokeColor,
  midX,
  midY,
}: {
  icon?: string;
  text?: string;
  /** Override for the icon glyph colour; defaults to the edge stroke. */
  iconColor?: string;
  /** Override for the text colour; defaults to the edge stroke. */
  textColor?: string;
  /** The edge's resolved stroke colour — the badge default. */
  strokeColor: string;
  midX: number;
  midY: number;
}) {
  const iconHtml = icon ? iconSvg(icon, 18) : "";
  const iconOnly = !!icon && !text;
  return (
    <foreignObject
      x={midX - EDGE_OVERLAY_W / 2}
      y={midY - EDGE_OVERLAY_H / 2}
      width={EDGE_OVERLAY_W}
      height={EDGE_OVERLAY_H}
      style={{ overflow: "visible", pointerEvents: "none" }}
    >
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          className="arch-edge-badge"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: icon && text ? 5 : 0,
            padding: iconOnly ? "2px" : text ? "3px 9px" : "4px",
            borderRadius: 999,
            background: "#0f172a",
            border: iconOnly ? "none" : `1px solid ${EDGE_DEFAULT}`,
            color: "#e2e8f0",
            fontSize: 11,
            fontWeight: 600,
            lineHeight: 1,
            whiteSpace: "nowrap",
            boxShadow: "0 1px 3px rgba(0,0,0,0.45)",
          }}
        >
          {icon && (
            <span
              style={{
                display: "inline-flex",
                width: 18,
                height: 18,
                color: iconColor ?? strokeColor,
              }}
              dangerouslySetInnerHTML={{ __html: iconHtml }}
            />
          )}
          {text && <span style={{ color: textColor ?? strokeColor }}>{text}</span>}
        </div>
      </div>
    </foreignObject>
  );
}

/**
 * Mounts a registered custom element for the edge. Element receives:
 *   - kebab-cased attributes for every widget.<name> prop on the edge,
 *   - DOM property `el.archEdge` carrying structured edge data,
 *   - DOM property `el.archPath` with the rendered SVG path string.
 */
function EdgeTagWidget({
  edge,
  tag,
  pathD,
  scale,
}: {
  edge: BoardEdge;
  tag: string;
  pathD: string;
  scale: number;
}) {
  const ref = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    (el as unknown as { archEdge?: unknown }).archEdge = {
      from: edge.from,
      to: edge.to,
      id: edge.id,
      details: edge.details,
    };
    (el as unknown as { archPath?: string }).archPath = pathD;
  }, [edge, pathD]);

  const attrs: Record<string, string> = {};
  if (edge.widgetProps) {
    for (const [k, v] of edge.widgetProps) {
      attrs[propNameToAttr(k)] = propValueToAttr(v);
    }
  }

  return createElement(tag, {
    ref,
    style: {
      display: "block",
      maxWidth: "100%",
      ...bandCssVars(scale),
    },
    ...attrs,
  });
}

/**
 * Inline-HTML overlay rendered via dangerouslySetInnerHTML. {{path}}
 * substitution runs once per render; values are HTML-escaped by
 * default ({{x}}) or raw ({{{x}}}). The contract matches what module
 * templates get, plus `from` / `to` / `details` from the edge.
 */
function EdgeTemplateWidget({
  edge,
  template,
  scale,
}: {
  edge: BoardEdge;
  template: string;
  scale: number;
}) {
  const html = useMemo(
    () =>
      applyEdgeTemplate(template, {
        from: edge.from,
        to: edge.to,
        edgeId: edge.id,
      }),
    [edge.id, edge.from, edge.to, template]
  );
  return (
    <div
      style={{
        width: "100%",
        textAlign: "center",
        ...bandCssVars(scale),
      }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

/**
 * Strict {{path}} substitution for edge templates. Mirrors the
 * module-side logic but with edge-specific context fields. Unknown
 * paths render empty; double braces escape, triple braces inject raw.
 */
function applyEdgeTemplate(
  template: string,
  ctx: { from: string; to: string; edgeId: string }
): string {
  const triple = /\{\{\{\s*([^}\s][^}]*?)\s*\}\}\}/g;
  const dbl = /\{\{\s*([^}\s][^}]*?)\s*\}\}/g;
  const lookup = (token: string): string | undefined => {
    const t = token.trim();
    if (t === "from") return ctx.from;
    if (t === "to") return ctx.to;
    if (t === "id") return ctx.edgeId;
    return undefined;
  };
  const escape = (s: string): string =>
    s.replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  return template
    .replace(triple, (_, p: string) => lookup(p) ?? "")
    .replace(dbl, (_, p: string) => escape(lookup(p) ?? ""));
}

export function resolveEdgeStyle({
  edgeSel,
  isOut,
  isIn,
  inBlast,
  inProcess,
  offPath,
  isCurrentStep,
  stepScrubActive,
  processActive,
  widgetStyle,
  isAspect,
  ground,
}: {
  edgeSel: boolean;
  isOut: boolean;
  isIn: boolean;
  inBlast: boolean;
  inProcess: boolean;
  offPath: boolean;
  isCurrentStep: boolean;
  stepScrubActive: boolean;
  processActive: boolean;
  widgetStyle?: EdgeWidgetStyle;
  isAspect?: boolean;
  /** View `focus`: this line runs in the GROUND (an edge is figure only when
   *  BOTH its endpoints are). A TIER, not an override — it sits below every
   *  interaction state, so a selected or scrubbed ground edge still reads, and
   *  above the resting appearance, which it scales by the same factor the veil
   *  takes off a ground node. */
  readonly ground?: boolean;
}) {
  // Interaction states win over per-interface widget config — selection,
  // process scrub, blast radius are about *what the user is currently
  // doing*, not the model itself. Widget style is the *baseline*: it
  // sets the resting appearance and surfaces (e.g. dashed) carry through
  // to interaction states because they describe the line shape, not its
  // emphasis colour.
  const dashed = widgetStyle?.dashed === true;

  // Aspect-web edges keep their own aspect colour when emphasised — the
  // generic out/in/blast palette (cyan/green/orange) would read as a
  // dependency edge and lie about what the line means. Only the weight /
  // opacity change with interaction state; the hue stays the aspect's.
  if (isAspect) {
    const stroke = widgetStyle?.color || EDGE_DEFAULT;
    if (processActive && !inProcess)
      return { stroke, width: 1.2, opacity: 0.06, dashed: true };
    if (edgeSel)
      return { stroke, width: 3, opacity: 1, dashed: true };
    if (inBlast || isOut || isIn)
      return { stroke, width: 2.5, opacity: 0.95, dashed: true };
    const resting = widgetStyle?.opacity ?? 0.4;
    return {
      stroke,
      width: widgetStyle?.width ?? 1.5,
      opacity: ground ? groundStrokeOpacity(resting) : resting,
      dashed: true,
    };
  }

  if (processActive && !inProcess) {
    return { stroke: EDGE_DEFAULT, width: 2, opacity: 0.08, dashed };
  }
  // In the process but only on branches the walker did not select: visible
  // enough to read the alternative, clearly quieter than the taken path.
  if (processActive && offPath && !edgeSel) {
    return { stroke: EDGE_DEFAULT, width: 2, opacity: 0.12, dashed };
  }
  if (stepScrubActive) {
    if (isCurrentStep)
      return { stroke: EDGE_SELECTED, width: 6, opacity: 1, dashed };
    // In-process but not current step: dim somewhat
    return { stroke: EDGE_DEFAULT, width: 2.5, opacity: 0.18, dashed };
  }
  if (edgeSel)
    return { stroke: EDGE_SELECTED, width: 5, opacity: 0.95, dashed };
  // Direction wins over blast: an edge that DIRECTLY leaves/enters the selected
  // node keeps its out(amber)/in(green) colour. Blast (yellow) is only for the
  // INDIRECT edges pulled in by the dependency highlight — otherwise selecting a
  // node paints all its own in/out edges one flat blast colour.
  if (isOut)
    return { stroke: EDGE_OUT, width: 3.5, opacity: 0.95, dashed };
  if (isIn)
    return { stroke: EDGE_IN, width: 3.5, opacity: 0.95, dashed };
  if (inBlast)
    return { stroke: EDGE_BLAST, width: 3.5, opacity: 0.9, dashed };

  // No interaction override — widget style takes the resting appearance
  // (color, width, opacity, dashed). Anything missing falls back to the
  // generic defaults so a partially-styled interface still looks sane.
  const resting = widgetStyle?.opacity ?? 0.5;
  return {
    stroke: widgetStyle?.color || EDGE_DEFAULT,
    width: widgetStyle?.width ?? 2.5,
    opacity: ground ? groundStrokeOpacity(resting) : resting,
    dashed,
  };
}

function ArrowheadMarker({ id, color }: { id: string; color: string }) {
  return (
    <marker
      id={id}
      viewBox="0 0 10 10"
      refX="9"
      refY="5"
      markerWidth="8"
      markerHeight="8"
      orient="auto-start-reverse"
      markerUnits="userSpaceOnUse"
    >
      {/* Explicit fill per marker — NOT fill="context-stroke", which Safari
          doesn't support (arrowheads vanish there). `color` may be a hex or
          a `rgb(var(--x))` expression; it goes through style (a CSS
          property) because var() does not resolve in presentation
          attributes. */}
      <path d="M 0 0 L 10 5 L 0 10 Z" style={{ fill: color }} />
    </marker>
  );
}

// =============================================================================
// Step chip + edge decoration overlay (process walker badges, pulse)
// =============================================================================
//
// StepChip renders the edge's step number(s) — ONE chip per edge, fed by the
// walker's selected-walk indices when decorated, the details' indices
// otherwise. Multi-step edges render a pill listing every index (the old
// min-only circle hid all but the lowest — on branching processes the visible
// number often belonged to a step the scrubber never reached).
//
// EdgeDecoration renders the walker extras AROUND the chip: modifier icons
// (loop ↻, parallel ⇉, catch ring), branch label pill, bind tag, terminator
// badge, and the pulse for active / collapsed-group / parallel-group states.
// Both are positioned in SVG space at the edge's on-path midpoint.

const CHIP_R = 16;

/** One geometry for the chip AND the ornaments around it — a multi-number pill
 *  widens past CHIP_R, and badges keyed to the constant would land on it. */
function chipGeometry(label: string, current: boolean) {
  const single = label.length <= 2;
  const r = current ? CHIP_R + 4 : CHIP_R;
  const halfW = single ? r : Math.max(r, label.length * 5.5 + 8);
  return { label, single, r, halfW };
}

function StepChip({
  label: labelText,
  offPath,
  compensation,
  isCurrentStep,
  activeStepIndex,
  mx,
  my,
  onClick,
}: {
  label: string;
  offPath: boolean;
  compensation: boolean;
  isCurrentStep: boolean;
  activeStepIndex: number | null;
  mx: number;
  my: number;
  onClick: (evt: React.MouseEvent) => void;
}) {
  const { label, single, r, halfW } = chipGeometry(labelText, isCurrentStep);
  const dimmed = activeStepIndex !== null && !isCurrentStep;
  const fill = offPath
    ? "#475569"
    : compensation
      ? "#b45309"
      : isCurrentStep
        ? "#0284c7"
        : "#0ea5e9";
  return (
    <g onClick={onClick}>
      {isCurrentStep && (
        /* Halo ring behind the badge for the active step. */
        <rect
          x={mx - halfW - 6}
          y={my - r - 6}
          width={(halfW + 6) * 2}
          height={(r + 6) * 2}
          rx={r + 6}
          fill="rgba(14, 165, 233, 0.25)"
          stroke="#0ea5e9"
          strokeOpacity={0.6}
          strokeWidth={2}
        />
      )}
      <rect
        x={mx - halfW}
        y={my - r}
        width={halfW * 2}
        height={r * 2}
        rx={r}
        fill={fill}
        stroke="white"
        strokeWidth={isCurrentStep ? 3 : 2}
        strokeDasharray={offPath ? "3 3" : undefined}
        // While scrubbing, off-path chips must dim BELOW on-path idle ones —
        // the branch not taken can never be the loudest thing on the board.
        opacity={offPath ? (dimmed ? 0.15 : 0.55) : dimmed ? 0.25 : 1}
      />
      <text
        x={mx}
        y={my}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={single ? (isCurrentStep ? 22 : 18) : 14}
        fontWeight={isCurrentStep ? 800 : 700}
        fill="white"
        opacity={offPath ? (dimmed ? 0.25 : 0.7) : dimmed ? 0.4 : 1}
        style={{ userSelect: "none" }}
      >
        {label}
      </text>
    </g>
  );
}

function EdgeDecoration({
  deco,
  edgeMid,
}: {
  deco: EdgeDeco;
  edgeMid: { x: number; y: number };
}) {
  const isActive = deco.state.kind === "active";
  const isCollapsed = deco.state.kind === "collapsed-group";
  const isParallel = deco.state.kind === "parallel-group";
  const isTerminator = deco.state.kind === "terminator";
  const pulse = isActive || isCollapsed || isParallel;
  // Ornaments sit relative to the ACTUAL chip extent — a multi-index pill is
  // wider than the base circle, and the active chip is taller.
  const { r, halfW } = chipGeometry(chipLabelFor(deco), isActive);

  return (
    <g transform={`translate(${edgeMid.x},${edgeMid.y})`}>
      {/* Modifier badges — placed clear of the chip. */}
      {deco.modifiers.loop && (
        <text x={halfW + 6} y={-r + 4} fontSize={12} fill="#fbbf24">↻</text>
      )}
      {deco.modifiers.parallel && (
        <text x={halfW + 18} y={-r + 4} fontSize={12} fill="#a5b4fc">⇉</text>
      )}
      {deco.modifiers.catch && (
        <rect
          x={-halfW - 5}
          y={-r - 5}
          width={(halfW + 5) * 2}
          height={(r + 5) * 2}
          rx={r + 5}
          fill="none"
          stroke="#f87171"
          strokeWidth={1.5}
        />
      )}
      {deco.modifiers.branchLabels.length > 0 && (
        <g transform={`translate(${halfW + 2},${r - 4})`}>
          <rect width={Math.max(deco.modifiers.branchLabels.join(",").length * 5 + 8, 22)} height={12} rx={6} fill="rgba(15,23,42,0.85)" stroke="#475569" />
          <text x={4} y={9} fontSize={9} fill="#cbd5e1">{deco.modifiers.branchLabels.join(",")}</text>
        </g>
      )}
      {deco.modifiers.binds.length > 0 && (
        <text x={-halfW - 4} y={r + 10} textAnchor="end" fontSize={9} fill="#64748b">= {deco.modifiers.binds.join(",")}</text>
      )}
      {isTerminator && deco.state.kind === "terminator" && (
        <g transform={`translate(${halfW + 14},${-r + 2})`}>
          <circle cx={6} cy={4} r={7} fill={deco.state.type === "fail" ? "#ef4444" : "#10b981"} />
          <text x={6} y={8} textAnchor="middle" fontSize={10} fill="#fff">
            {deco.state.type === "fail" ? "✕" : "✓"}
          </text>
        </g>
      )}
      {pulse && (
        <circle cx={0} cy={0} r={halfW + 2} fill="none" stroke="rgba(56,189,248,0.8)" strokeWidth={2}>
          <animate attributeName="r" from={halfW + 2} to={halfW + 16} dur="0.9s" repeatCount="indefinite" />
          <animate attributeName="opacity" from={0.9} to={0} dur="0.9s" repeatCount="indefinite" />
        </circle>
      )}
    </g>
  );
}
